import { FolderKanban } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useProjectSubmissions } from '../queries';

/**
 * Staff dashboard card: how many team results are waiting for a grade, and the
 * queue itself — each row links to the project whose review panel grades it
 * (there is no separate review screen; the panel lives on the project page).
 */
export function ProjectsToGradeCard() {
  const { t } = useTranslation(['projects']);
  const query = useProjectSubmissions({ needs_grading: true, page_size: 5 });

  const rows = query.data?.results ?? [];
  const count = query.data?.count ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('projects:cards.toGradeTitle')}</CardTitle>
        <CardDescription>{t('projects:cards.toGradeDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {query.isLoading ? (
          <div className="space-y-2" aria-hidden>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : count === 0 ? (
          <p className="text-muted-foreground text-sm">{t('projects:cards.toGradeEmpty')}</p>
        ) : (
          <>
            <p className="text-sm font-medium tabular-nums">
              {t('projects:cards.toGradeCount', { count })}
            </p>
            <ul className="space-y-1">
              {rows.map(submission => (
                <li key={submission.public_id}>
                  <Link
                    to={`/projects/${submission.project.public_id}`}
                    className="hover:bg-accent flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                  >
                    <FolderKanban className="text-muted-foreground size-4 shrink-0" />
                    <span className="font-medium">{submission.project.title}</span>
                    <span className="flex-1" />
                    <span className="text-muted-foreground">{t('projects:queue.open')}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Button variant="link" size="sm" asChild className="px-2">
              <Link to="/projects">{t('projects:cards.openQueue')}</Link>
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
